import { capitalizeFirstLetter } from 'services/string'

interface FormatWhatsAppLinkOptions {
  slug?: string
  customMessage?: string
}

/**
 * Formata um link do WhatsApp de acordo com as opções `options` fornecidas. Particularmente útil em páginas direcionadas, como Landing Pages e páginas de público específico.
 * @param link link do WhatsApp a ser formatado.
 * @param options opções de customização do link.
 * * `slug` identificador inserido no final da mensagem.
 * * `customMessage` mensagem customizada que substitui a mensagem padrão.
 */
export const formatWhatsAppLink = (link: string, options: FormatWhatsAppLinkOptions) => {
  const { slug, customMessage } = options
  if (link === '') return ''
  if (!slug && !customMessage) return link

  const urlObject = new URL(link)
  urlObject.searchParams.set(
    'text',
    customMessage
      ? customMessage
      : slug
        ? `${urlObject.searchParams.get('text')} (${capitalizeFirstLetter(slug)})`
        : 'Quero saber mais sobre a Hubs Contabilidade',
  )
  return urlObject.toString()
}

/** Moeda BRL: 1234.56 → "R$ 1.234,56" */
export const formatCurrencyBRL = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number.isFinite(value) ? value : 0)

/** Número pt-BR com n casas (default 2): 1234.5 → "1.234,50" */
export const formatDecimalBR = (value: number, digits = 2) =>
  new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number.isFinite(value) ? value : 0)

/**
 * Percentual: aceita fração (0.11) ou valor (11) → "11,00%"
 */
export const formatPercent = (value: number, opts?: { min?: number; max?: number }) => {
  const n = Number.isFinite(value) ? value : 0
  const normalized = n > 1 ? n : n * 100
  const { min = 0, max = 2 } = opts ?? {}
  return (
    new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: min,
      maximumFractionDigits: max,
    }).format(normalized) + '%'
  )
}

/**
 * Converte um valor qualquer para string **apenas** quando houver conteúdo.
 * - Se o valor for `null` ou `undefined`, retorna `undefined` para não exibir
 *   "undefined"/"null" como placeholder no UI.
 * - Para qualquer outro valor, retorna `String(v)`.
 *
 */
export const toPlaceholderString = (v: unknown) => (v == null ? undefined : String(v))

/**
 * Arredonda para 2 casas decimais e retorna Number.
 * - Normaliza entradas não finitas para 0
 * - Corrige erros de ponto flutuante (ex.: 1.005 → 1.01)
 * - Aceita number | string | null | undefined
 */
export const roundTo = (v: number | string | null | undefined, decimals = 2) => {
  const n = Number(v ?? 0)
  if (!Number.isFinite(n)) return 0
  const f = 10 ** decimals
  return Math.round((n + Number.EPSILON) * f) / f
}

/**
 * Percentual pt-BR “flexível”: aceita fração (0.28) ou valor já em % (28) e formata como "28%" (máx. 2 casas).
 *
 * Regras:
 * - Se `r > 1`, assume que já está em percentual (ex.: 28 -> "28%").
 * - Caso contrário, trata como fração e multiplica por 100 (ex.: 0.28 -> "28%").
 *
 */
export const formatAsPercentage = (r: number) => {
  const value = r > 1 ? r : r * 100
  return value.toLocaleString('pt-BR', { maximumFractionDigits: 2 }) + '%'
}
